import { parseIQThinkingText } from "./parseIQThinkingText";

const DISPLAY_LINE_COUNT = 4;
const MAX_SINGLE_LINE_LENGTH = 120;

/** IQ streams JSON payloads on `event: text`; never show those as thinking (same as MiniIQOverlay). */
export function isResponsePayloadText(text) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("```")) return true;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return true;
  if (/"steps"\s*:/.test(trimmed)) return true;
  if (/"questions"\s*:/.test(trimmed)) return true;
  if (/"step_type"\s*:/.test(trimmed)) return true;
  if (/"what_to_look_for"\s*:/.test(trimmed)) return true;
  return false;
}

export function stripMarkdownForBannerLine(text) {
  return String(text ?? "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/^[-*]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isHeadingParagraph(text) {
  const trimmed = String(text ?? "").trim();
  return trimmed.length > 0 && trimmed.length <= 72 && !/[.!?]$/.test(trimmed);
}

/** Insert paragraph breaks before inline markdown headings from concatenated thought chunks. */
function normalizeReasoningParagraphs(reasoning) {
  return String(reasoning ?? "")
    .replace(/([.!?])(\*\*[^*\n]+\*\*)/g, "$1\n\n$2")
    .replace(/([^\n])(\*\*[^*\n]+\*\*)/g, "$1\n\n$2");
}

function splitParagraphBlocks(readableReasoning) {
  const normalized = normalizeReasoningParagraphs(readableReasoning).trim();
  if (!normalized) return [];

  const doubleSplit = normalized
    .split(/\n\s*\n+/)
    .map((block) => stripMarkdownForBannerLine(block))
    .filter(Boolean);

  if (doubleSplit.length > 1) {
    return doubleSplit;
  }

  const singleLines = normalized
    .split(/\n/)
    .map((line) => stripMarkdownForBannerLine(line))
    .filter(Boolean);

  if (
    singleLines.length > 1 &&
    singleLines.every((line) => line.length <= MAX_SINGLE_LINE_LENGTH)
  ) {
    return singleLines;
  }

  const fallback = stripMarkdownForBannerLine(normalized);
  return fallback ? [fallback] : [];
}

/**
 * Turn readable markdown reasoning into paragraph display lines.
 */
function buildVisualLines(readableReasoning) {
  return splitParagraphBlocks(readableReasoning);
}

/**
 * Show streamed IQ reasoning in batches of 4 lines: lines fill the batch top-down and,
 * once a 5th line arrives, the batch is cleared and refills from the first slot.
 *
 * @param {string} reasoning
 * @returns {{ kind: 'skeleton' | 'text', text?: string, isHeading?: boolean }[]}
 */
export function formatIQThinkingBannerLines(reasoning) {
  const slots = Array.from({ length: DISPLAY_LINE_COUNT }, () => ({ kind: "skeleton" }));

  // The response body streams on the same channel as the thinking; parsing drops it.
  const readableReasoning = parseIQThinkingText(reasoning);
  if (!readableReasoning || isResponsePayloadText(readableReasoning)) {
    return slots;
  }

  const visualLines = buildVisualLines(readableReasoning);
  if (!visualLines.length) return slots;

  const batchStart = Math.floor((visualLines.length - 1) / DISPLAY_LINE_COUNT) * DISPLAY_LINE_COUNT;
  const batch = visualLines.slice(batchStart, batchStart + DISPLAY_LINE_COUNT);

  return slots.map((slot, index) => {
    const line = batch[index];
    if (!line) return slot;

    const entry = { kind: "text", text: line };
    if (isHeadingParagraph(line)) {
      entry.isHeading = true;
    }
    return entry;
  });
}
